'use client';

/**
 * Chain context provider + hook (Sequential Gate G2, client layer).
 *
 * The route-derived chain value, exposed to deep children without prop-drilling.
 * There is NO `useState`-backed "current chain" — the value is read from
 * `usePathname()` (mirroring `Navbar.tsx`), so back/forward, deep-links, and SSR all
 * stay correct because the URL is canonical. The provider only memoizes the registry
 * lookup; it holds no mutable state.
 *
 * See docs/superpowers/specs/hub-build-scope/04-sequential-gate-and-contracts.md §2/§3.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import {
  resolveChainContext,
  type ChainContextValue,
} from '@/lib/hub/chain-context';

const ChainContext = createContext<ChainContextValue | null>(null);

/**
 * Optional provider. Memoizes the registry lookup for the current route and shares it
 * with deep children. Mounting it is optional — `useChainContext()` falls back to
 * reading `usePathname()` directly when no provider is present, so the value is always
 * available.
 */
export function ChainContextProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const value = useMemo(() => resolveChainContext(pathname), [pathname]);
  return <ChainContext.Provider value={value}>{children}</ChainContext.Provider>;
}

/**
 * Read the current chain context. Uses the nearest `ChainContextProvider` if present,
 * otherwise derives the value directly from the route — so it works with or without
 * the provider. Returns `{ chain: ChainId | 'all'; meta?: ChainMeta }`.
 */
export function useChainContext(): ChainContextValue {
  const ctx = useContext(ChainContext);
  const pathname = usePathname();
  // Hooks must run unconditionally; the fallback value is cheap to memoize.
  const fallback = useMemo(() => resolveChainContext(pathname), [pathname]);
  return ctx ?? fallback;
}
